import * as Tooltip from '@radix-ui/react-tooltip'
import { Plus, RotateCcw, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { TEST_TYPE } from '@/lib/constants/test-types'
import {
  type ClassOperationItem,
  isClassOperation,
  type ProblemTestCase,
} from '@/lib/types/test-cases'
import { cn } from '@/lib/utils'
import type { ClassExample } from '../problem-context'
import { isClassExample, useProblemOptional } from '../problem-context'
import { useTestCaseInjection } from '../test-case-injection-context'
import { useTestCasesOptional } from '../test-cases-context'
import { ClassTestCaseEditor } from './class-test-case-editor'
import { TestCaseDetails } from './test-case-details'
import { TestCaseTabs } from './test-case-tabs'
import type {
  EditableClassOperation,
  EditableClassTestCase,
  TestCasesPaneProps,
} from './types'
import {
  buildCaseInputMap,
  cloneTestCase,
  createExpectedValueMap,
  createInputValueMap,
  getDefaultInputValue,
  MAX_TEST_CASE_TABS,
  parseExpectedValue,
  stringifyValue,
} from './utils'

export function TestCasesPane({ problemSlug }: TestCasesPaneProps) {
  const testCasesContext = useTestCasesOptional()
  const problemContext = useProblemOptional()

  // Check test type
  const testConfig = problemContext?.data?.testConfig
  const isClassBased = testConfig?.testType === TEST_TYPE.CLASS
  const isRaw = testConfig?.testType === TEST_TYPE.RAW

  // ==========================================================================
  // Class-based test state
  // ==========================================================================

  // Get class examples from MDX frontmatter
  const classExamples = useMemo<ClassExample[]>(() => {
    const examples = problemContext?.data?.examples
    if (!examples) return []
    return examples.filter(isClassExample)
  }, [problemContext?.data?.examples])

  // Initialize class test cases from examples or create empty default
  const initialClassTestCases = useMemo<EditableClassTestCase[]>(() => {
    if (classExamples.length > 0) {
      return classExamples.map((ex, idx) => ({
        id: `${problemSlug ?? 'class'}-case-${idx}`,
        constructorArgs: ex.constructorArgs ?? '[]',
        // Filter out comments - only include executable operations
        operations: ex.operations.filter(isClassOperation).map((op) => ({
          id: crypto.randomUUID(),
          type: 'operation' as const,
          method: op.method,
          args: op.args,
          expected: op.expected,
          expectsError: op.expectsError,
        })),
      }))
    }
    // Default empty test case
    return [
      {
        id: `${problemSlug ?? 'class'}-case-0`,
        constructorArgs: '[]',
        operations: [],
      },
    ]
  }, [classExamples, problemSlug])

  const [classTestCases, setClassTestCases] = useState<EditableClassTestCase[]>(
    () => initialClassTestCases,
  )
  const [activeClassCaseIndex, setActiveClassCaseIndex] = useState(0)
  const [hasCustomClassCases, setHasCustomClassCases] = useState(false)

  // Reset class test cases when problem changes
  useEffect(() => {
    setClassTestCases(initialClassTestCases)
    setActiveClassCaseIndex(0)
    setHasCustomClassCases(false)
  }, [initialClassTestCases])

  // ==========================================================================
  // Class test case handlers
  // ==========================================================================

  const handleAddClassCase = useCallback(() => {
    if (classTestCases.length >= MAX_TEST_CASE_TABS) return

    const newCase: EditableClassTestCase = {
      id: `${problemSlug ?? 'class'}-case-${Date.now()}`,
      constructorArgs: '[]',
      operations: [],
    }
    setClassTestCases((prev) => [...prev, newCase])
    setActiveClassCaseIndex(classTestCases.length)
    setHasCustomClassCases(true)
  }, [classTestCases.length, problemSlug])

  const handleCloseClassCase = useCallback(
    (index: number) => {
      if (classTestCases.length <= 1) return

      setClassTestCases((prev) => prev.filter((_, i) => i !== index))
      setActiveClassCaseIndex((current) => {
        if (current >= index && current > 0) {
          return current - 1
        }
        return current
      })
      setHasCustomClassCases(true)
    },
    [classTestCases.length],
  )

  const handleResetClassCases = useCallback(() => {
    setClassTestCases(initialClassTestCases)
    setActiveClassCaseIndex(0)
    setHasCustomClassCases(false)
  }, [initialClassTestCases])

  const handleUpdateConstructorArgs = useCallback(
    (caseIndex: number, value: string) => {
      setClassTestCases((prev) =>
        prev.map((tc, i) =>
          i === caseIndex ? { ...tc, constructorArgs: value } : tc,
        ),
      )
    },
    [],
  )

  const handleAddOperation = useCallback((caseIndex: number) => {
    const newOp: EditableClassOperation = {
      id: crypto.randomUUID(),
      method: '',
      args: '[]',
      expected: 'null',
    }
    setClassTestCases((prev) =>
      prev.map((tc, i) =>
        i === caseIndex ? { ...tc, operations: [...tc.operations, newOp] } : tc,
      ),
    )
    setHasCustomClassCases(true)
  }, [])

  const handleUpdateOperation = useCallback(
    (
      caseIndex: number,
      opIndex: number,
      updates: Partial<ClassOperationItem>,
    ) => {
      setClassTestCases((prev) =>
        prev.map((tc, i) =>
          i === caseIndex
            ? {
                ...tc,
                operations: tc.operations.map((op, j) =>
                  j === opIndex
                    ? ({ ...op, ...updates } as EditableClassOperation)
                    : op,
                ),
              }
            : tc,
        ),
      )
    },
    [],
  )

  const handleRemoveOperation = useCallback(
    (caseIndex: number, opIndex: number) => {
      setClassTestCases((prev) =>
        prev.map((tc, i) =>
          i === caseIndex
            ? {
                ...tc,
                operations: tc.operations.filter((_, j) => j !== opIndex),
              }
            : tc,
        ),
      )
      setHasCustomClassCases(true)
    },
    [],
  )

  const handleMoveOperation = useCallback(
    (caseIndex: number, opIndex: number, direction: 'up' | 'down') => {
      const targetIndex = direction === 'up' ? opIndex - 1 : opIndex + 1
      setClassTestCases((prev) =>
        prev.map((tc, i) => {
          if (i !== caseIndex) return tc
          if (targetIndex < 0 || targetIndex >= tc.operations.length) return tc

          const newOps = [...tc.operations]
          const [movedOp] = newOps.splice(opIndex, 1)
          newOps.splice(targetIndex, 0, movedOp)
          return { ...tc, operations: newOps }
        }),
      )
    },
    [],
  )

  // ==========================================================================
  // Sync class test cases to shared context (for Run mode)
  // ==========================================================================

  const setClassTestCasesRef = testCasesContext?.setClassTestCases

  useEffect(() => {
    if (!isClassBased || !setClassTestCasesRef) return

    // Transform to format expected by test harness
    const runClassTestCases = classTestCases.map((tc) => ({
      constructorArgs: tc.constructorArgs,
      operations: tc.operations,
    }))
    setClassTestCasesRef(runClassTestCases)
  }, [isClassBased, classTestCases, setClassTestCasesRef])

  // ==========================================================================
  // Raw test state
  // ==========================================================================

  // Get raw test scripts from test config (keyed by language)
  const initialRawScripts = useMemo<string[]>(() => {
    if (!isRaw || !testConfig?.rawExampleScripts) return []
    // Default to python scripts
    return testConfig.rawExampleScripts
      .map((scripts) => scripts.python ?? '')
      .filter(Boolean)
  }, [isRaw, testConfig?.rawExampleScripts])

  const [rawScripts, setRawScripts] = useState<string[]>(
    () => initialRawScripts,
  )
  // Stable IDs for raw script tabs (avoids index-as-key lint issues)
  const rawScriptIds = useMemo(
    () => rawScripts.map((_, i) => `raw-${problemSlug ?? 'script'}-${i}`),
    [problemSlug, rawScripts],
  )
  const [activeRawIndex, setActiveRawIndex] = useState(0)
  const [hasCustomRawScripts, setHasCustomRawScripts] = useState(false)

  useEffect(() => {
    setRawScripts(initialRawScripts)
    setActiveRawIndex(0)
    setHasCustomRawScripts(false)
  }, [initialRawScripts])

  // Sync raw scripts to shared context
  const setRawTestScriptsRef = testCasesContext?.setRawTestScripts

  useEffect(() => {
    if (!isRaw || !setRawTestScriptsRef) return
    setRawTestScriptsRef(rawScripts)
  }, [isRaw, rawScripts, setRawTestScriptsRef])

  // ==========================================================================
  // Function-based test state (existing logic)
  // ==========================================================================

  // Detect in-place modification problems via the test config's outputType
  // (declared here before the useEffect that references it)
  const isInPlace = testConfig?.outputType === 'void'

  // Get test cases from ProblemContext (now sourced from DB via tRPC)
  const baseCases = useMemo<ProblemTestCase[]>(() => {
    const testCases = problemContext?.data?.testCases
    if (testCases && testCases.length > 0) {
      return [...testCases]
    }
    return []
  }, [problemContext?.data?.testCases])

  const [cases, setCases] = useState<ProblemTestCase[]>(() => baseCases)
  const [activeCaseId, setActiveCaseId] = useState<string | null>(
    () => baseCases[0]?._id ?? null,
  )
  const [inputValues, setInputValues] = useState<
    Map<string, Map<string, string>>
  >(() => createInputValueMap(baseCases))
  const [expectedValues, setExpectedValues] = useState<Map<string, string>>(
    () => createExpectedValueMap(baseCases),
  )
  const [hasCustomCases, setHasCustomCases] = useState(false)

  useEffect(() => {
    setCases(baseCases)
    setActiveCaseId(baseCases[0]?._id ?? null)
    setInputValues(createInputValueMap(baseCases))
    setExpectedValues(createExpectedValueMap(baseCases))
    setHasCustomCases(false)
  }, [baseCases])

  // Get stable reference to setFunctionTestCases to avoid infinite loops
  const setFunctionTestCasesRef = testCasesContext?.setFunctionTestCases

  // Sync current test cases to shared context (for Run mode to access)
  // Note: Only runs for function-based problems; class/raw sync is handled above
  useEffect(() => {
    if (isClassBased || isRaw) return
    if (!setFunctionTestCasesRef) return

    // Transform cases into the format expected by the Run API
    const runTestCases = cases.map((testCase) => {
      // Build inputs object from the inputValues map
      const caseInputs = inputValues.get(testCase._id)
      const inputs: Record<string, string> = {}
      testCase.inputs.forEach((input) => {
        inputs[input.name] =
          caseInputs?.get(input.name) ?? getDefaultInputValue(input.example)
      })

      // For in-place problems, the return value is always None/null so
      // expectedOutput is meaningless — the harness compares modified inputs instead.
      const expectedOutput = isInPlace
        ? 'null'
        : (expectedValues.get(testCase._id) ??
          stringifyValue(testCase.expected))

      // Include expectedInputsAfter for in-place modification problems
      const expectedInputsAfter = testCase.expectedInputsAfter
        ? Object.fromEntries(
            Object.entries(testCase.expectedInputsAfter).map(([k, v]) => [
              k,
              JSON.stringify(v),
            ]),
          )
        : undefined

      return { inputs, expectedOutput, expectedInputsAfter }
    })

    setFunctionTestCasesRef(runTestCases)
  }, [
    isClassBased,
    isRaw,
    cases,
    inputValues,
    expectedValues,
    setFunctionTestCasesRef,
    isInPlace,
  ])

  // ==========================================================================
  // Handle injected test cases from cross-pane "Use Testcase" button
  // ==========================================================================

  const injection = useTestCaseInjection()

  useEffect(() => {
    if (isClassBased) return
    if (!injection?.pendingInjection) return
    if (cases.length >= MAX_TEST_CASE_TABS) {
      injection.clearInjection()
      return
    }

    const { inputs, expectedOutput } = injection.pendingInjection
    const templateCase = cases[0] ?? baseCases[0]
    if (!templateCase) {
      injection.clearInjection()
      return
    }

    const newCaseId = `${problemSlug ?? 'injected'}-case-${Date.now()}`
    const newCase = cloneTestCase(templateCase, newCaseId)

    // Build input values map from injected inputs
    const caseInputMap = new Map<string, string>()
    for (const input of newCase.inputs) {
      caseInputMap.set(input.name, inputs[input.name] ?? '')
    }

    setCases((prev) => [...prev, newCase])
    setInputValues((prev) => {
      const next = new Map(prev)
      next.set(newCaseId, caseInputMap)
      return next
    })
    setExpectedValues((prev) => {
      const next = new Map(prev)
      next.set(newCaseId, expectedOutput)
      return next
    })
    setActiveCaseId(newCaseId)
    setHasCustomCases(true)
    injection.clearInjection()
  }, [
    injection?.pendingInjection,
    isClassBased,
    cases,
    baseCases,
    problemSlug,
    injection,
  ])

  const activeCase =
    cases.find((testCase) => testCase._id === activeCaseId) ?? cases[0] ?? null
  const resolvedActiveCaseId = activeCase?._id ?? null

  const handleAddCase = () => {
    if (cases.length >= MAX_TEST_CASE_TABS) {
      return
    }
    const templateCase =
      cases.find((testCase) => testCase._id === resolvedActiveCaseId) ??
      cases[cases.length - 1] ??
      baseCases[0] ??
      null
    const newCaseId = `${problemSlug ?? 'custom'}-case-${Date.now()}`
    const newCase = cloneTestCase(templateCase, newCaseId)
    const blankValues = new Map<string, string>()
    newCase.inputs.forEach((input) => {
      blankValues.set(input.name, '')
    })

    setCases((prev) => [...prev, newCase])
    setInputValues((prev) => {
      const next = new Map(prev)
      next.set(newCaseId, buildCaseInputMap(newCase.inputs, blankValues))
      return next
    })
    setExpectedValues((prev) => {
      const next = new Map(prev)
      next.set(newCaseId, '')
      return next
    })
    setActiveCaseId(newCaseId)
    setHasCustomCases(true)
  }

  const handleCloseCase = (caseId: string) => {
    const removalIndex = cases.findIndex((testCase) => testCase._id === caseId)
    if (removalIndex === -1) return

    const nextCases = cases.filter((testCase) => testCase._id !== caseId)

    setCases(nextCases)
    setInputValues((prev) => {
      const next = new Map(prev)
      next.delete(caseId)
      return next
    })
    setExpectedValues((prev) => {
      const next = new Map(prev)
      next.delete(caseId)
      return next
    })
    setActiveCaseId((current) => {
      if (current !== caseId) {
        return current
      }
      if (!nextCases.length) {
        return null
      }
      const fallbackIndex = Math.min(
        Math.max(removalIndex, 0),
        nextCases.length - 1,
      )
      return nextCases[fallbackIndex]?._id ?? nextCases[0]?._id ?? null
    })
    setHasCustomCases(true)
  }

  const handleResetCases = () => {
    setCases(baseCases)
    setActiveCaseId(baseCases[0]?._id ?? null)
    setInputValues(createInputValueMap(baseCases))
    setExpectedValues(createExpectedValueMap(baseCases))
    setHasCustomCases(false)
  }

  const showResetButton = hasCustomCases || cases.length !== baseCases.length
  const disableAddCase = cases.length >= MAX_TEST_CASE_TABS
  const isOriginalCase =
    resolvedActiveCaseId !== null &&
    baseCases.some((testCase) => testCase._id === resolvedActiveCaseId)

  const getInputValue = (caseId: string | null, inputName: string): string => {
    if (!caseId) return ''
    const caseInputs = inputValues.get(caseId)
    if (caseInputs) {
      return caseInputs.get(inputName) ?? ''
    }
    const testCase = cases.find((c) => c._id === caseId)
    const input = testCase?.inputs.find((i) => i.name === inputName)
    if (input) {
      return getDefaultInputValue(input.example)
    }
    return ''
  }

  const getExpectedValue = (caseId: string | null): string => {
    if (!caseId) return ''
    const stored = expectedValues.get(caseId)
    if (stored !== undefined) {
      return stored
    }
    const testCase = cases.find((tc) => tc._id === caseId)
    return stringifyValue(testCase?.expected)
  }

  const updateInputValue = (
    caseId: string | null,
    inputName: string,
    value: string,
  ) => {
    if (!caseId) return
    setInputValues((prev) => {
      const next = new Map(prev)
      const caseInputs = new Map(next.get(caseId) ?? [])
      caseInputs.set(inputName, value)
      next.set(caseId, caseInputs)
      return next
    })
  }

  const updateExpectedValue = (caseId: string | null, value: string) => {
    if (!caseId) return
    setExpectedValues((prev) => {
      const next = new Map(prev)
      next.set(caseId, value)
      return next
    })
    setCases((prev) =>
      prev.map((testCase) =>
        testCase._id === caseId
          ? { ...testCase, expected: parseExpectedValue(value) }
          : testCase,
      ),
    )
  }

  // ==========================================================================
  // Raw test UI
  // ==========================================================================

  if (isRaw && testConfig) {
    const showRawResetButton =
      hasCustomRawScripts || rawScripts.length !== initialRawScripts.length

    return (
      <section className="flex h-full flex-col" aria-label="Raw test cases">
        <header className="border-border/60 border-b px-4 py-2">
          <nav
            className="flex items-center gap-2"
            aria-label="Raw test case navigation"
          >
            <div className="flex-1">
              <div
                role="tablist"
                aria-label="Raw test cases"
                className="flex flex-wrap items-center gap-2 pr-2"
              >
                {rawScriptIds.map((id, rawIdx) => {
                  const isActive = activeRawIndex === rawIdx
                  return (
                    <div
                      key={id}
                      role="tab"
                      tabIndex={0}
                      aria-selected={isActive}
                      onClick={() => setActiveRawIndex(rawIdx)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setActiveRawIndex(rawIdx)
                        }
                      }}
                      className={`focus-visible:ring-ring relative inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none ${
                        isActive
                          ? 'border-border text-foreground bg-black/5 dark:bg-white/10'
                          : 'text-muted-foreground hover:text-foreground border-transparent hover:bg-black/5 dark:hover:bg-white/10'
                      }`}
                    >
                      <span className="select-none">Case {rawIdx + 1}</span>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {showRawResetButton && (
                <button
                  type="button"
                  onClick={() => {
                    setRawScripts(initialRawScripts)
                    setActiveRawIndex(0)
                    setHasCustomRawScripts(false)
                  }}
                  className="border-border text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              )}
            </div>
          </nav>
        </header>

        <main className="flex-1 overflow-auto p-4">
          {rawScripts[activeRawIndex] !== undefined ? (
            <div className="flex h-full flex-col gap-2">
              <label className="text-muted-foreground text-xs font-medium">
                Test Script (Python)
              </label>
              <textarea
                value={rawScripts[activeRawIndex]}
                onChange={(e) => {
                  const newScripts = [...rawScripts]
                  newScripts[activeRawIndex] = e.target.value
                  setRawScripts(newScripts)
                  setHasCustomRawScripts(true)
                }}
                className="bg-muted text-foreground border-border flex-1 resize-none rounded-md border p-3 font-mono text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none"
                spellCheck={false}
              />
            </div>
          ) : (
            <section
              className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3 text-center"
              aria-label="No test cases available"
            >
              <p>No test scripts defined for this problem.</p>
            </section>
          )}
        </main>
      </section>
    )
  }

  // ==========================================================================
  // Class-based test UI
  // ==========================================================================

  if (isClassBased && testConfig) {
    const activeClassCase = classTestCases[activeClassCaseIndex]
    const showClassResetButton =
      hasCustomClassCases ||
      classTestCases.length !== initialClassTestCases.length

    return (
      <section className="flex h-full flex-col" aria-label="Class test cases">
        {/* Tab header for class test cases */}
        <header className="border-border/60 border-b px-4 py-2">
          <nav
            className="flex items-center gap-2"
            aria-label="Class test case navigation"
          >
            <div className="flex-1">
              <div
                role="tablist"
                aria-label="Class test cases"
                className="flex flex-wrap items-center gap-2 pr-2"
              >
                {classTestCases.map((tc, index) => {
                  const isActive = activeClassCaseIndex === index
                  return (
                    <div
                      key={tc.id}
                      role="tab"
                      tabIndex={0}
                      aria-selected={isActive}
                      onClick={() => setActiveClassCaseIndex(index)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setActiveClassCaseIndex(index)
                        }
                      }}
                      className={`group focus-visible:ring-ring relative inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none ${
                        isActive
                          ? 'border-border text-foreground bg-black/5 dark:bg-white/10'
                          : 'text-muted-foreground hover:text-foreground border-transparent hover:bg-black/5 dark:hover:bg-white/10'
                      }`}
                    >
                      <span className="select-none">Case {index + 1}</span>
                      {classTestCases.length > 1 && (
                        <button
                          type="button"
                          aria-label={`Close Case ${index + 1}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            handleCloseClassCase(index)
                          }}
                          className={cn(
                            'border-border bg-background text-muted-foreground hover:bg-foreground/10 hover:text-foreground focus-visible:ring-ring dark:bg-muted absolute -top-1.5 -right-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border shadow-sm focus-visible:ring-2 focus-visible:outline-none',
                            REVEAL_ON_HOVER,
                          )}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {showClassResetButton && (
                <button
                  type="button"
                  onClick={handleResetClassCases}
                  className="border-border text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              )}
              <Tooltip.Provider delayDuration={150}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      type="button"
                      onClick={handleAddClassCase}
                      disabled={classTestCases.length >= MAX_TEST_CASE_TABS}
                      className="border-border text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add Case
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Content
                    side="top"
                    align="center"
                    className="z-[100] border-border bg-background text-foreground rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
                  >
                    Add new empty test case
                    <Tooltip.Arrow className="fill-background" />
                  </Tooltip.Content>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          </nav>
        </header>

        {/* Class test case editor */}
        <main className="flex-1 overflow-auto p-4">
          {activeClassCase ? (
            <ClassTestCaseEditor
              testCase={activeClassCase}
              testConfig={testConfig}
              onUpdateConstructorArgs={(value) =>
                handleUpdateConstructorArgs(activeClassCaseIndex, value)
              }
              onAddOperation={() => handleAddOperation(activeClassCaseIndex)}
              onUpdateOperation={(opIndex, updates) =>
                handleUpdateOperation(activeClassCaseIndex, opIndex, updates)
              }
              onRemoveOperation={(opIndex) =>
                handleRemoveOperation(activeClassCaseIndex, opIndex)
              }
              onMoveOperation={(opIndex, direction) =>
                handleMoveOperation(activeClassCaseIndex, opIndex, direction)
              }
            />
          ) : (
            <section
              className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3 text-center"
              aria-label="No test cases available"
            >
              <p>No test cases defined.</p>
              <p className="text-sm">
                Click <strong>Add Case</strong> to create a test case.
              </p>
            </section>
          )}
        </main>
      </section>
    )
  }

  // ==========================================================================
  // Function-based test UI (existing)
  // ==========================================================================

  return (
    <section className="flex h-full flex-col" aria-label="Test cases">
      <TestCaseTabs
        cases={cases}
        activeCaseId={resolvedActiveCaseId}
        onSelect={setActiveCaseId}
        onCloseCase={handleCloseCase}
        onAddCase={handleAddCase}
        onResetCases={handleResetCases}
        showResetButton={showResetButton}
        disableAddCase={disableAddCase}
      />
      <main className="flex-1 overflow-auto p-4">
        {activeCase ? (
          <TestCaseDetails
            testCase={activeCase}
            activeCaseId={resolvedActiveCaseId}
            getInputValue={getInputValue}
            updateInputValue={updateInputValue}
            expectedValue={getExpectedValue(resolvedActiveCaseId)}
            onExpectedChange={(nextValue) =>
              updateExpectedValue(resolvedActiveCaseId, nextValue)
            }
            isOriginalCase={isOriginalCase}
            isInPlace={isInPlace}
          />
        ) : (
          <section
            className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3 text-center"
            aria-label="No test cases available"
          >
            <p>No test cases loaded for this problem.</p>
            <p className="text-sm">
              Click <strong>Add Case</strong> to create custom tests, or{' '}
              <strong>Submit</strong> to run against all saved test cases.
            </p>
          </section>
        )}
      </main>
    </section>
  )
}

// Re-export types for external use
export type { TestCasesPaneProps } from './types'
